/**
 * Offline-first Shelter Check-in Service
 *
 * Handles creating, storing, and syncing shelter resident check-ins
 * when the device is offline. Uses IndexedDB for local storage and
 * syncs to the server when connectivity is restored.
 */

import { db, OfflineCheckin } from './db';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { isClientIdConflict, isDeletedReplay, newClientId, originatorStamp, mustDeferSend, sendTenantOf } from './client-id';

function getActiveTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Mirrors specialNeedsTypeEnum in backend/src/modules/shelters/shelter.schemas.ts.
 * Anything outside this set is rejected by the server, so filtering here keeps
 * one unrecognized value from blocking the whole resident.
 */
const VALID_SPECIAL_NEEDS = [
  'mobility',
  'oxygen',
  'dialysis',
  'insulin',
  'cognitive',
  'sensory_vision',
  'sensory_hearing',
  'mental_health',
  'pediatric',
  'bariatric',
  'other_medical',
] as const;

/**
 * Accept an array (current form) or a legacy comma-joined string (records
 * already queued on field devices), and keep only server-valid members.
 */
export function normalizeSpecialNeeds(raw: unknown): string[] {
  const parts = Array.isArray(raw)
    ? raw.map((x) => String(x))
    : String(raw ?? '').split(',');
  const cleaned = parts.map((s) => s.trim()).filter(Boolean);
  return [...new Set(cleaned.filter((s) => (VALID_SPECIAL_NEEDS as readonly string[]).includes(s)))];
}

export interface CheckinInput {
  shelter_id: string;
  tenant_id: string;
  resident_name: string;
  /** Name parts as typed. Optional so legacy call sites still compile. */
  first_name?: string;
  last_name?: string;
  resident_count: number;
  phone?: string;
  /** Array of specialNeedsTypeEnum members; `string` accepted for legacy rows. */
  special_needs?: string | string[];
  /**
   * Everything else the check-in form captures. These MUST be carried end to
   * end: a field that CheckinInput cannot hold is dropped at save time, and the
   * record is still marked synced, so the resident's medical needs and
   * emergency contact are lost permanently and silently.
   */
  date_of_birth?: string;
  gender?: string;
  has_medical_needs?: boolean;
  medical_notes?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  notes?: string;
  pets?: string[];
}

export interface CachedShelterInfo {
  id: string;
  name: string;
  capacity: number;
  current_occupancy: number;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  duplicates: number;
  errors: string[];
}

const SHELTER_CACHE_KEY = 'offline_shelter_cache';

class ShelterCheckinOfflineService {
  /**
   * Cache shelter data for offline reference (capacity, names, etc.)
   */
  async cacheShelterData(shelters: CachedShelterInfo[]): Promise<void> {
    try {
      localStorage.setItem(SHELTER_CACHE_KEY, JSON.stringify({
        data: shelters,
        cached_at: new Date().toISOString(),
      }));
    } catch (error) {
      console.warn('[ShelterCheckin] Failed to cache shelter data:', error);
    }
  }

  /**
   * Get cached shelter info for offline display
   */
  getCachedShelters(): CachedShelterInfo[] {
    try {
      const raw = localStorage.getItem(SHELTER_CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return parsed.data || [];
    } catch {
      return [];
    }
  }

  /**
   * Create an offline check-in record
   */
  async create(input: CheckinInput): Promise<OfflineCheckin> {
    // P2-05: client id (sent as the resident's id, so a replayed check-in can
    // never admit the person twice) + originator stamps.
    const stamp = originatorStamp(input.tenant_id);
    const now = stamp.occurred_at;

    const checkin: OfflineCheckin = {
      ...stamp,
      shelter_id: input.shelter_id,
      tenant_id: input.tenant_id,
      resident_name: input.resident_name,
      first_name: input.first_name,
      last_name: input.last_name,
      resident_count: input.resident_count,
      phone: input.phone,
      special_needs: input.special_needs,
      notes: input.notes,
      // Life-safety fields the field form captures. This object is built
      // key-by-key, so anything omitted here is DISCARDED at save time and the
      // row is still flipped is_synced — deletable by clearSyncedCache. A
      // resident checked in on a dead cell site would reach the EOC roster with
      // no medical needs and no next-of-kin, under a success toast, and the
      // medical branch triaging that roster would see no medical flags.
      // syncCheckin already forwards all of these; dropping them here is what
      // made the round trip lossy.
      date_of_birth: input.date_of_birth,
      gender: input.gender,
      has_medical_needs: input.has_medical_needs,
      medical_notes: input.medical_notes,
      emergency_contact_name: input.emergency_contact_name,
      emergency_contact_phone: input.emergency_contact_phone,
      pets: input.pets || [],
      is_synced: 0,
      created_at: now,
      updated_at: now,
    };

    const localId = await db.offlineCheckins.add(checkin);
    checkin.localId = localId;

    // Update cached shelter occupancy optimistically
    this.incrementCachedOccupancy(input.shelter_id, input.resident_count);

    // Try immediate sync if online
    if (networkService.isOnline()) {
      await this.syncCheckin(localId);
    }

    return checkin;
  }

  /**
   * Get all offline check-ins, optionally filtered by shelter
   */
  async getAll(shelterId?: string): Promise<OfflineCheckin[]> {
    if (shelterId) {
      return db.offlineCheckins
        .where('shelter_id')
        .equals(shelterId)
        .toArray();
    }
    return db.offlineCheckins.toArray();
  }

  /**
   * Get a single check-in by local ID
   */
  async getById(localId: number): Promise<OfflineCheckin | undefined> {
    return db.offlineCheckins.get(localId);
  }

  /**
   * Get count of unsynced check-ins
   */
  async getUnsyncedCount(): Promise<number> {
    return db.offlineCheckins
      .where('is_synced')
      .equals(0)
      .count();
  }

  /**
   * Sync all pending check-ins to the server.
   * Uses duplicate detection by matching resident_name + shelter_id
   * to prevent double check-ins after reconnection.
   */
  async syncAll(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      duplicates: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    const pending = await db.offlineCheckins
      .where('is_synced')
      .equals(0)
      .toArray();

    const activeTenantId = getActiveTenantId();

    for (const checkin of pending) {
      if (checkin.localId == null) continue;
      // Tenant guard: the api client sends the ambient X-Tenant-ID, so a
      // check-in queued under another tenant waits until that tenant is active.
      if (checkin.tenant_id != null && activeTenantId != null && checkin.tenant_id !== activeTenantId) continue;
      // P2-05: a check-in another user recorded on this device is never
      // replayed as the user signed in now (it stays queued for its originator).
      if (mustDeferSend(checkin)) continue;

      try {
        const synced = await this.syncCheckin(checkin.localId);
        if (synced === 'synced') {
          result.synced++;
        } else if (synced === 'duplicate') {
          result.duplicates++;
        } else {
          result.failed++;
          result.errors.push(`Failed to sync check-in for "${checkin.resident_name}"`);
        }
      } catch (error) {
        result.failed++;
        const msg = error instanceof Error ? error.message : 'Sync failed';
        result.errors.push(`"${checkin.resident_name}": ${msg}`);
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  /**
   * Sync a single check-in record to the server.
   * Returns 'synced', 'duplicate', or 'failed'.
   */
  private async syncCheckin(localId: number): Promise<'synced' | 'duplicate' | 'failed'> {
    const checkin = await db.offlineCheckins.get(localId);
    if (!checkin || checkin.is_synced === 1) return 'synced';
    if (mustDeferSend(checkin)) return 'failed';

    try {
      // P2-05: a check-in queued by an older build has no client id. Give it
      // one and persist it BEFORE the first send, so every retry from now on
      // carries the same id and the server returns the same resident (200)
      // instead of admitting the person twice.
      let clientId = checkin.client_id;
      if (!clientId) {
        clientId = newClientId();
        await db.offlineCheckins.update(localId, { client_id: clientId });
      }

      // Prefer the name parts exactly as the responder typed them. The
      // whitespace split is a LEGACY-ONLY fallback for records queued by builds
      // that stored just `resident_name`: it misfiles multi-word given names and
      // compound surnames ("Maria Del Carmen" / "Ruiz Vega"), which is how a
      // resident becomes unfindable on the roster during a family reunification.
      const nameParts = checkin.resident_name.trim().split(/\s+/);
      const firstName = checkin.first_name?.trim() || nameParts[0] || '';
      const lastName = checkin.last_name?.trim() || nameParts.slice(1).join(' ') || '';

      // Normalize defensively rather than trusting the stored shape.
      //
      // The capture form used to join selections into one string
      // ("mobility, oxygen"), and this wrapped that string in a single-element
      // array. The backend takes an array of specialNeedsTypeEnum members, so
      // any check-in with TWO OR MORE special needs 400'd — permanently, on
      // every reconnect, because only 409 is handled below. A medically fragile
      // resident (oxygen, dialysis) never reached the shelter roster the EOC
      // sweeps during a power outage or evacuation.
      //
      // Records captured by older builds are still sitting in IndexedDB on
      // field devices in the joined-string form, so normalization has to happen
      // here at read time, not only at the (now fixed) write site.
      const needs = normalizeSpecialNeeds(checkin.special_needs);

      // EVERY resident field persisted on the offline record must be forwarded
      // here. Anything left out is destroyed on success: the POST 201s and the
      // row is flipped is_synced=1 (and becomes deletable by clearSyncedCache),
      // so a resident checked in on a dead cell site would reach the EOC roster
      // with no medical flags and no next-of-kin, and nothing would ever say so.
      // `offline_created_at` is deliberately NOT sent — createResidentSchema has
      // no such key, so validate() strips it; sending it only invites confusion.
      const payload: Record<string, unknown> = {
        id: clientId,
        // Device time of the check-in (P2-04; the server bounds it).
        occurred_at: checkin.occurred_at || checkin.created_at,
        shelter_id: checkin.shelter_id,
        first_name: firstName,
        last_name: lastName,
        has_special_needs: needs.length > 0,
        special_needs: needs,
      };

      // Optional on createResidentSchema — send only what was actually captured.
      const optional: Record<string, unknown> = {
        phone: checkin.phone || undefined,
        date_of_birth: checkin.date_of_birth || undefined,
        gender: checkin.gender || undefined,
        has_medical_needs: checkin.has_medical_needs,
        medical_notes: checkin.medical_notes || undefined,
        emergency_contact_name: checkin.emergency_contact_name || undefined,
        emergency_contact_phone: checkin.emergency_contact_phone || undefined,
        notes: checkin.notes || undefined,
      };
      for (const [key, value] of Object.entries(optional)) {
        if (value !== undefined) payload[key] = value;
      }

      await api.post(`/shelters/${checkin.shelter_id}/residents`, payload, sendTenantOf(checkin));

      await db.offlineCheckins.update(localId, {
        is_synced: 1,
        updated_at: new Date().toISOString(),
      });

      return 'synced';
    } catch (error: unknown) {
      // P2-05: with a client id, a replay of a check-in that already landed is
      // answered 200 with that resident, so "already on the server" is a
      // success above, never an error here. The old rule "any 409 = duplicate,
      // mark synced" is gone: a 409 now means the record did NOT land for this
      // user (CLIENT_ID_CONFLICT, or a server conflict), and marking it synced
      // would delete a resident's check-in from the device unsent.
      if (isDeletedReplay(error)) {
        // The check-in landed earlier and the resident record was deleted since.
        await db.offlineCheckins.update(localId, {
          is_synced: 1,
          updated_at: new Date().toISOString(),
        });
        return 'duplicate';
      }
      if (isClientIdConflict(error)) {
        console.warn('[ShelterCheckin] Client id conflict for localId', localId, '- kept for review');
        return 'failed';
      }

      console.error('[ShelterCheckin] Sync failed for localId', localId, error);
      return 'failed';
    }
  }

  /**
   * Optimistically update cached shelter occupancy
   */
  private incrementCachedOccupancy(shelterId: string, count: number): void {
    try {
      const shelters = this.getCachedShelters();
      const updated = shelters.map(s =>
        s.id === shelterId
          ? { ...s, current_occupancy: s.current_occupancy + count }
          : s
      );
      this.cacheShelterData(updated);
    } catch {
      // Non-critical -- ignore
    }
  }

  /**
   * Clear all synced records (keep pending)
   */
  async clearSyncedCache(): Promise<void> {
    await db.offlineCheckins
      .where('is_synced')
      .equals(1)
      .delete();
  }

  /**
   * Clear all cached data
   */
  async clearAll(): Promise<void> {
    await db.offlineCheckins.clear();
    localStorage.removeItem(SHELTER_CACHE_KEY);
  }
}

export const shelterCheckinOfflineService = new ShelterCheckinOfflineService();
